"use client";

import { ImageGallery, ImageGalleryCategory, ImageGalleryImage, useImageGallery } from "@/components/common/gallery";
import { GalleryCategories, GalleryImageCounter, GalleryInfoButton } from "@/components/common/gallery/overlays";
import type { MediaItem, SUPType } from "@/types/sup";
import { prepareGalleryImages } from "@/utils/sup/gallery";
import { unitSpaces } from "@/utils/sup/sort-media-list";
import * as React from "react";

interface GalleryProps {
	coverImage?: string;
	mediaList?: MediaItem[];
	projectId?: string;
	projectName?: string;
	className?: string;
	type?: SUPType;
	isLoading?: boolean;
	shouldPrioritizeCoverImage?: boolean; // Control whether to prioritize the first (cover) image
	sizes?: string;
	hideGalleryInfoButton?: boolean;
	onMainImageClick?: () => void;
	categories?: ImageGalleryCategory[];
}

const defaultCategories = [
	{ name: "Hall", code: "halls" },
	{ name: "Bedroom", code: "bedrooms" },
	{ name: "Balcony", code: "balconies" },
	{ name: "Kitchen", code: "kitchens" },
	{ name: "Amenities", code: "amenities" },
	{ name: "Bathroom", code: "bathrooms" },
	{ name: "Others", code: "others" },
	{ name: "Parking", code: "parkings" },
	{ name: "Pooja Room", code: "poojaRooms" },
	{ name: "Utility", code: "utilities" },
];

// Internal component that uses gallery context to sync category
function GalleryContent({
	images,
	sortedCategories,
	selectedCategory,
	setSelectedCategory,
	hideGalleryInfoButton,
}: {
	images: ImageGalleryImage[];
	sortedCategories: ImageGalleryCategory[];
	selectedCategory: ImageGalleryCategory | undefined;
	setSelectedCategory: (category: ImageGalleryCategory | undefined) => void;
	hideGalleryInfoButton?: boolean;
}) {
	const { currentIndex, isFullscreen } = useImageGallery();

	// Sync selected category with current image
	React.useEffect(() => {
		const currentImage = images[currentIndex];
		if (currentImage?.space) {
			const currentCategory = sortedCategories.find(cat => cat.code === currentImage.space);
			if (currentCategory && currentCategory.code !== selectedCategory?.code) {
				setSelectedCategory(currentCategory);
			}
		}
	}, [currentIndex, images, sortedCategories, selectedCategory, setSelectedCategory]);

	return (
		<>
			{/* Custom Image Counter */}
			<GalleryImageCounter
				className="opacity-100 md:opacity-0 md:group-hover/card:opacity-100 transition-opacity duration-200"
				showImageName={true}
			/>

			{/* Info Button */}
			{!hideGalleryInfoButton && <GalleryInfoButton />}

			{/* Category Navigation - Only show in fullscreen mode */}
			{isFullscreen && sortedCategories.length > 0 && (
				<GalleryCategories
					categories={sortedCategories}
					selectedCategory={selectedCategory}
					onCategoryChange={setSelectedCategory}
				/>
			)}
		</>
	);
}

export const GallerySmall = React.memo(function Gallery({
	coverImage,
	mediaList = [],
	type,
	projectId,
	className,
	isLoading = false,
	shouldPrioritizeCoverImage = true, // Default to false for below-the-fold content
	hideGalleryInfoButton = false, // Default to false
	sizes = "(max-width: 480px) 40vw, (max-width: 768px) 30vw, 320px",
	onMainImageClick,
	categories,
}: GalleryProps) {
	// Create the image list starting with cover image, then mediaList
	const images: ImageGalleryImage[] = React.useMemo(() => {
		return prepareGalleryImages({
			coverImage,
			mediaList,
			projectId,
			type,
			shouldPrioritizeCoverImage,
		});
	}, [coverImage, mediaList, projectId, type, shouldPrioritizeCoverImage]);

	// Sort categories by unitSpaces priority and filter to only those with images
	const sortedCategories = React.useMemo(() => {
		return (categories || defaultCategories)
			.filter(cat => images.some(img => img.space === cat.code))
			.sort((a, b) => {
				// Helper function to get category priority based on unitSpaces order
				const getCategoryPriority = (category: { name: string; code: string }): number => {
					const index = unitSpaces.indexOf(category.code);
					// If found in unitSpaces, return its index (lower index = higher priority)
					if (index !== -1) return index;

					// Other spaces (like 'others', 'amenities', 'parkings') get lower priority
					return 1000; // Large number to put them at the end
				};

				const priorityA = getCategoryPriority(a);
				const priorityB = getCategoryPriority(b);

				// If priorities are different, sort by priority
				if (priorityA !== priorityB) {
					return priorityA - priorityB;
				}

				// If same priority (both not in unitSpaces), sort by name alphabetically
				return a.name.localeCompare(b.name);
			});
	}, [categories, images]);

	// Track selected category - select the first category from sorted list
	const [selectedCategory, setSelectedCategory] = React.useState<ImageGalleryCategory | undefined>(sortedCategories[0]);

	if (isLoading) {
		return (
			<div className="w-full h-full bg-gray-100 animate-pulse">
				<div className="w-full h-full bg-gray-200"></div>
			</div>
		);
	}

	return (
		<ImageGallery
			images={images}
			className={`w-full h-full ${className}`}
			showNavigationButtons={images.length > 1}
			showImageCounter={false} // We'll use custom counter
			enableKeyboardNavigation={true}
			enableDoubleTapFullscreen={true}
			autoLoop={true}
			// for modern devices (retina/mobile), it will automaticaly download image of 2x or 3x size
			sizes={sizes}
			onMainImageClick={onMainImageClick}
		>
			<GalleryContent
				images={images}
				sortedCategories={sortedCategories}
				selectedCategory={selectedCategory}
				setSelectedCategory={setSelectedCategory}
				hideGalleryInfoButton={hideGalleryInfoButton}
			/>
		</ImageGallery>
	);
});
